import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { recoverAppointmentSchema, type RecoverAppointmentFormData } from '@geely/shared';
import { useRecoverAppointment } from '@/hooks';
import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@geely/ui';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { AxiosError } from 'axios';
import { MaskedCpfInput } from '@/app/components/booking/MaskedCpfInput';

interface RecoverAppointmentDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export function RecoverAppointmentDialog({ isOpen, onClose }: RecoverAppointmentDialogProps) {
  const [submitted, setSubmitted] = useState(false);
  const [rateLimited, setRateLimited] = useState(false);
  const recoverMutation = useRecoverAppointment();

  const form = useForm<RecoverAppointmentFormData>({
    resolver: zodResolver(recoverAppointmentSchema),
    defaultValues: {
      customerEmail: '',
      customerCpf: '',
    },
  });

  useEffect(() => {
    if (isOpen) {
      form.reset();
      setSubmitted(false);
      setRateLimited(false);
    }
  }, [isOpen, form]);

  const onSubmit = async (data: RecoverAppointmentFormData) => {
    setRateLimited(false);
    setSubmitted(false);

    recoverMutation.mutate(data, {
      onSuccess: () => {
        setSubmitted(true);
        form.reset();
      },
      onError: (error) => {
        if (error instanceof AxiosError && error.response?.status === 429) {
          setRateLimited(true);
        }
      },
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Recuperar Código de Agendamento</DialogTitle>
          <DialogDescription>
            Informe seu email e CPF/CNPJ para receber o código do seu último agendamento por email.
          </DialogDescription>
        </DialogHeader>

        {submitted && (
          <Alert className="border-green-200 bg-green-50 text-green-800">
            <CheckCircle2 className="size-4 text-green-600" />
            <AlertDescription>
              Se houver um agendamento associado a este email e CPF, você receberá um email com as informações em breve.
            </AlertDescription>
          </Alert>
        )}

        {rateLimited && (
          <Alert variant="destructive">
            <AlertCircle className="size-4" />
            <AlertDescription>
              Muitas tentativas. Tente novamente em alguns minutos.
            </AlertDescription>
          </Alert>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="space-y-4 py-2">
              <FormField
                control={form.control}
                name="customerEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="email"
                        placeholder="seu@email.com"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="customerCpf"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>CPF/CNPJ</FormLabel>
                    <FormControl>
                      <MaskedCpfInput
                        name={field.name}
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter className="mt-4">
              <Button type="button" variant="outline" onClick={onClose} disabled={recoverMutation.isPending}>
                Cancelar
              </Button>
              <Button type="submit" disabled={recoverMutation.isPending}>
                {recoverMutation.isPending ? 'Enviando...' : 'Recuperar Código'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
